import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { GetSellerPaymentExportRequest } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { GetSellerPaymentExportResponse } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';

import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';

import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { startWith } from 'rxjs/operators';

import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import {
    mapTranferDetailsSortColumn,
    PayablesInput,
    SortDirection
} from '../../../../financing-graphql/src';
import { ExportType } from '../models/export-type';
import { IExportRequest } from '../models/export-request';

@Injectable({ providedIn: 'root' })
export class GetSellerPaymentExportService extends ProtoApiService<
    GetSellerPaymentExportRequest,
    GetSellerPaymentExportResponse
> {
    public requestType = GetSellerPaymentExportRequest;
    public responseType = GetSellerPaymentExportResponse;
    public endpointName = 'GetSellerPaymentExport';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v2/financingExport';
                    this.serviceName = 'reportCenter';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'GetSellerPaymentExport';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: GetSellerPaymentExportRequest
    ): IExportRequest | GetSellerPaymentExportRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const input: PayablesInput = {
                companyFilter: {
                    supplierIds: [req.refSellerKey]
                },
                hasTransferId: req.refFundingBatchKey,
                pagination: {
                    skip: skip || 0,
                    take: take || 0,
                    sortColumn: new EnumType(mapTranferDetailsSortColumn(sortColumn)),
                    sortDirection: new EnumType(
                        SortDirection[sortDirection] ?? SortDirection.ASCENDING
                    )
                }
            };

            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        isGroup: true,
                        name: true,
                        supplierExternalId: true,
                        supplierCompanyName: true,
                        buyerCompanyName: true,
                        transfers: {
                            id: true,
                            payeeBankAccount: {
                                bankName: true,
                                accountNumber: true
                            }
                        },
                        transferDetails: {
                            paymentDate: true,
                            transferType: true
                        },
                        approvals: {
                            supplierDiscountFee: {
                                cents: true,
                                currencyCode: true
                            },
                            supplierDiscountFeeRate: true,
                            transferDetails: {
                                supplierPaymentAmount: {
                                    cents: true,
                                    currencyCode: true
                                },
                                transferType: true
                            },
                            adjustedAmount: {
                                cents: true,
                                currencyCode: true
                            },
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            originalDueDate: true,
                            invoice: {
                                date: true,
                                invoiceNumber: true
                            }
                        },
                        creditMemos: {
                            id: true,
                            creditMemoNumber: true,
                            allocatedAmount: {
                                cents: true,
                                currencyCode: true
                            }
                        }
                    }
                }
            });
            return {
                fileType: 'csv',
                exportType: ExportType.SUPPLIER_PAYMENT,
                query: `{${query}}`
            };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
